import React, { JSX, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import {
  ChartBarIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { LoadingPage, LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { CursorPager } from 'components/admin/CursorPager'
import { AdminListToolbar, ChipRow, PageSizePicker, SearchBox, ToolbarChip } from 'components/admin/AdminListToolbar'
import { SortHeader, SortDirection } from 'components/admin/SortHeader'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import {
  AccountDeletionPage,
  AccountDeletionPreview,
  AccountDeletionRow,
  AccountDeletionStatus,
  AccountUsagePage,
  AccountUsageRow,
  AccountUsageHistoryRow,
  FleetMonth,
  ScheduledAccountDeletion,
  SetAccountSuspendedResult,
  UsageCounters
} from 'types/api'
import {
  DELETE_BUTTON_SCHEDULED_LABEL,
  DELETE_BUTTON_SCHEDULED_TITLE,
  DELETE_BUTTON_TITLE,
  DELETIONS_CARD_HELP,
  DIALOG_LOADING,
  DIALOG_PREVIEW_ERROR,
  SCHEDULED_CARD_EMPTY,
  SCHEDULED_CARD_HEADING,
  SCHEDULED_CARD_HELP,
  cancelConfirmCopy,
  cancelledNotice,
  deleteDialogCopy,
  deletionRowNote,
  deletionStatusLabel,
  kindLabel,
  runningNotice,
  scheduledByLabel,
  scheduledNotice,
  scheduledStatusCopy,
  scheduledTopLine
} from 'lib/accountDeletionCopy'
import {
  createPagerGate,
  createRequestRace,
  pageNumberOf,
  parseChoice,
  parseFlag,
  parsePageSize,
  popToken,
  pushToken,
  resetTokens,
  tokenForPage
} from 'lib/adminListState'
import listAccountUsagePageQuery from 'graphql/listAccountUsagePage.graphql'
import listUsageOutliersQuery from 'graphql/listUsageOutliers.graphql'
import getFleetUsageQuery from 'graphql/getFleetUsage.graphql'
import getAccountUsageHistoryQuery from 'graphql/getAccountUsageHistory.graphql'
import setAccountSuspendedMutation from 'graphql/setAccountSuspended.graphql'
import requestAccountDeletionMutation from 'graphql/requestAccountDeletion.graphql'
import retryAccountDeletionMutation from 'graphql/retryAccountDeletion.graphql'
import listAccountDeletionsPageQuery from 'graphql/listAccountDeletionsPage.graphql'
import cancelAccountDeletionMutation from 'graphql/cancelAccountDeletion.graphql'
import listScheduledAccountDeletionsQuery from 'graphql/listScheduledAccountDeletions.graphql'
import previewAccountDeletionQuery from 'graphql/previewAccountDeletion.graphql'

const OUTLIER_THRESHOLD_CENTS = 500

const YT_DAILY_QUOTA_BUDGET_UNITS = 10000

const HISTORY_MONTHS = 13

const RUNBOOK_REMINDER = 'Runbook reminder: remove the streamer role in Auth0 and rotate event overlay tokens for any live events.'

interface DriverPart {
  counter: keyof UsageCounters
  unitCents: number
}

interface Driver {
  key: string
  label: string
  barClass: string
  parts: DriverPart[]
}

const DRIVERS: Driver[] = [
  { key: 'votes', label: 'Votes', barClass: 'bg-brand', parts: [{ counter: 'votesIngested', unitCents: 0.0015 }] },
  {
    key: 'uploads',
    label: 'Uploads',
    barClass: 'bg-sky-400',
    parts: [
      { counter: 'uploads', unitCents: 0.002 },
      { counter: 'uploadBytes', unitCents: 0 }
    ]
  },
  {
    key: 'youtube',
    label: 'YouTube',
    barClass: 'bg-red-400',
    parts: [
      { counter: 'ytUnits', unitCents: 0 },
      { counter: 'ytSessionsStarted', unitCents: 0.001 },
      { counter: 'ytSessionSeconds', unitCents: 0.000013 }
    ]
  },
  { key: 'republishes', label: 'Republishes', barClass: 'bg-amber-400', parts: [{ counter: 'republishes', unitCents: 0.01 }] },
  {
    key: 'links',
    label: 'Links',
    barClass: 'bg-emerald-400',
    parts: [
      { counter: 'overlayTokenMints', unitCents: 0.0007 },
      { counter: 'ticketExchanges', unitCents: 0.0005 }
    ]
  },
  {
    key: 'creates',
    label: 'Creates',
    barClass: 'bg-violet-400',
    parts: [
      { counter: 'eventsCreated', unitCents: 0.002 },
      { counter: 'chatOverlaysCreated', unitCents: 0.002 },
      { counter: 'emoteWallsCreated', unitCents: 0.002 },
      { counter: 'merchOverlaysCreated', unitCents: 0.002 },
      { counter: 'pollsCreated', unitCents: 0.002 },
      { counter: 'tugsCreated', unitCents: 0.002 },
      { counter: 'boardsCreated', unitCents: 0.002 }
    ]
  }
]

const COUNTER_GROUPS: Array<{ label: string, counters: Array<keyof UsageCounters>, format?: (n: number) => string }> = [
  { label: 'Votes ingested', counters: ['votesIngested'] },
  { label: 'Uploads', counters: ['uploads'] },
  { label: 'Upload bytes (gross volume)', counters: ['uploadBytes'], format: (n) => formatBytes(n) },
  { label: 'Republishes', counters: ['republishes'] },
  { label: 'Overlay links (mints + exchanges)', counters: ['overlayTokenMints', 'ticketExchanges'] },
  { label: 'YouTube units', counters: ['ytUnits'] },
  { label: 'YouTube sessions started', counters: ['ytSessionsStarted'] },
  { label: 'YouTube session seconds', counters: ['ytSessionSeconds'] },
  {
    label: 'Entities created',
    counters: [
      'eventsCreated', 'chatOverlaysCreated', 'emoteWallsCreated',
      'merchOverlaysCreated', 'pollsCreated', 'tugsCreated', 'boardsCreated'
    ]
  }
]

const counterValue = (counters: UsageCounters | null | undefined, key: keyof UsageCounters): number => {
  const n = counters?.[key]
  return typeof n === 'number' && Number.isFinite(n) ? n : 0
}

const driverCents = (counters: UsageCounters | null | undefined, driver: Driver): number =>
  driver.parts.reduce((sum, p) => sum + counterValue(counters, p.counter) * p.unitCents, 0)

const groupValue = (counters: UsageCounters | null | undefined, keys: Array<keyof UsageCounters>): number =>
  keys.reduce((sum, k) => sum + counterValue(counters, k), 0)

const formatCents = (cents: number): string =>
  cents >= 100 ? `$${(cents / 100).toFixed(2)}` : `${cents.toFixed(2)}¢`

const formatEstimate = (cents: number): string => `$${(cents / 100).toFixed(2)}`

const MIB = 1024 * 1024
const GIB = 1024 * MIB
const formatBytes = (bytes: number): string => {
  if (bytes >= GIB) return `${(bytes / GIB).toFixed(1)} GB`
  if (bytes >= MIB) return `${(bytes / MIB).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${Math.round(bytes)} B`
}

const monthKeyOf = (d: Date): string =>
  `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`

const lastMonths = (n: number): string[] => {
  const out: string[] = []
  const now = new Date()
  let y = now.getUTCFullYear()
  let m = now.getUTCMonth()
  for (let i = 0; i < n; i++) {
    out.unshift(`${y}${String(m + 1).padStart(2, '0')}`)
    m -= 1
    if (m < 0) {
      m = 11
      y -= 1
    }
  }
  return out
}

const daysInMonth = (yyyymm: string): number => {
  const y = Number(yyyymm.slice(0, 4))
  const m = Number(yyyymm.slice(4, 6))
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}

const monthLabel = (yyyymm: string): string => `${yyyymm.slice(0, 4)}-${yyyymm.slice(4, 6)}`

const quotaSharePct = (ytUnits: number, yyyymm: string): number => {
  const monthlyBudget = YT_DAILY_QUOTA_BUDGET_UNITS * daysInMonth(yyyymm)
  return monthlyBudget > 0 ? Math.round((ytUnits / monthlyBudget) * 1000) / 10 : 0
}

const suspendedOf = (row: AccountUsageRow): boolean =>
  row.suspendedAt != null

const emailOf = (row: AccountUsageRow): string | null => row.email ?? null

const DELETE_CONFIRM_WORD = 'DELETE'

const DELETION_IN_FLIGHT_STATUSES = new Set(['PENDING', 'RUNNING'])

const REASON_CODE_PATTERN = /^[A-Za-z0-9_.:-]{1,64}$/

interface FailedStep {
  name: string
  reason: string | null
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const parseSteps = (steps: unknown): Record<string, unknown> | null => {
  if (isRecord(steps)) return steps
  if (typeof steps !== 'string' || steps === '') return null
  try {
    const parsed: unknown = JSON.parse(steps)
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

const humanizeStepName = (key: string): string =>
  key
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim()

const reasonCodeOf = (v: Record<string, unknown>): string | null => {
  for (const field of ['reason', 'code', 'error', 'errorName']) {
    const candidate = v[field]
    if (typeof candidate === 'string' && REASON_CODE_PATTERN.test(candidate)) return candidate
  }
  return null
}

const failedStepsOf = (deletion: AccountDeletionStatus): FailedStep[] => {
  const steps = parseSteps(deletion.steps)
  if (steps === null) return []
  const workerYouTube = steps.youtube
  const youtubeRerunOk = isRecord(workerYouTube) && workerYouTube.status === 'ok'
  const out: FailedStep[] = []
  Object.entries(steps).forEach(([key, value]) => {
    if (key === 'youtubeRequest' && youtubeRerunOk) return
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'failed') out.push({ name: humanizeStepName(key), reason: null })
      return
    }
    if (!isRecord(value)) return
    const status = typeof value.status === 'string' ? value.status.toLowerCase() : null
    const failed = status === 'failed' || status === 'skipped' || value.ok === false
    const purgeIncomplete = value.purgeComplete === false
    if (!failed && !purgeIncomplete) return
    out.push({
      name: humanizeStepName(key),
      reason: reasonCodeOf(value) ?? (purgeIncomplete ? 'youtube-purge-incomplete' : null)
    })
  })
  return out
}

const formatStamp = (iso: string | null | undefined): string => {
  if (iso == null || iso === '') return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString()
}

const CardError = ({ message, onRetry }: { message: string, onRetry: () => void }): JSX.Element => (
  <div className='mt-3 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
    <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
    <span className='flex-1'>{message}</span>
    <button
      onClick={onRetry}
      className='shrink-0 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium hover:bg-red-500/20'
    >
      Retry
    </button>
  </div>
)

const RefreshButton = ({ onClick, loading, title, testid }: {
  onClick: () => void
  loading: boolean
  title: string
  testid: string
}): JSX.Element => (
  <button
    onClick={onClick}
    disabled={loading}
    title={title}
    data-testid={testid}
    className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
  >
    <ArrowPathIcon className={`size-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden='true' />
    Refresh
  </button>
)

const SuspendedBadge = (): JSX.Element => (
  <span className='inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-300 ring-1 ring-inset ring-red-500/30'>
    <ShieldExclamationIcon className='size-3' aria-hidden='true' />
    Suspended
  </span>
)

const AccountLabel = ({ row }: { row: AccountUsageRow }): JSX.Element => {
  const email = emailOf(row)
  if (email === null) {
    return (
      <span
        className='italic text-dm-text-accent'
        title='Email is stored when the account next logs in and backfilled in batches on each refresh; this row is not resolved yet'
      >
        email not resolved
      </span>
    )
  }
  return <span className='truncate text-dm-text-primary'>{email}</span>
}

const SuspendButton = ({ row, busy, onSuspend }: {
  row: AccountUsageRow
  busy: boolean
  onSuspend: (row: AccountUsageRow, suspended: boolean) => void
}): JSX.Element => {
  const suspended = suspendedOf(row)
  const title = suspended
    ? 'Unsuspend this account (confirm step follows)'
    : 'Suspend this account (confirm step follows)'
  return (
    <button
      type='button'
      data-testid={`usage-suspend-button-${row.accountId}`}
      onClick={(e) => {
        e.stopPropagation()
        onSuspend(row, !suspended)
      }}
      disabled={busy}
      title={title}
      className={suspended
        ? 'inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        : 'inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-[11px] font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'}
    >
      {busy && <LoadingSpinner />}
      {suspended ? 'Unsuspend' : 'Suspend'}
    </button>
  )
}

const DeleteButton = ({ row, busy, inFlight, scheduled, onDelete }: {
  row: AccountUsageRow
  busy: boolean
  inFlight: boolean
  scheduled: boolean
  onDelete: (row: AccountUsageRow) => void
}): JSX.Element => (
  <button
    type='button'
    data-testid={`usage-delete-button-${row.accountId}`}
    onClick={(e) => {
      e.stopPropagation()
      onDelete(row)
    }}
    disabled={busy || inFlight || scheduled}
    title={scheduled
      ? DELETE_BUTTON_SCHEDULED_TITLE
      : inFlight
        ? 'A deletion for this account is already in progress'
        : DELETE_BUTTON_TITLE}
    className='inline-flex items-center gap-1.5 rounded-md border border-red-500/40 px-2 py-1 text-[11px] font-medium text-red-300 hover:border-red-500/60 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'
  >
    {busy ? <LoadingSpinner /> : scheduled ? <ClockIcon className='size-3' aria-hidden='true' /> : <TrashIcon className='size-3' aria-hidden='true' />}
    {scheduled ? DELETE_BUTTON_SCHEDULED_LABEL : inFlight ? 'Deleting' : 'Delete'}
  </button>
)

const BarRow = ({ label, cents, maxCents, barClass }: {
  label: string
  cents: number
  maxCents: number
  barClass: string
}): JSX.Element => {
  const pct = maxCents > 0 ? Math.min(100, (cents / maxCents) * 100) : 0
  return (
    <div className='flex items-center gap-2 text-[11px]'>
      <span className='w-28 shrink-0 text-dm-text-secondary'>{label}</span>
      <div className='h-2.5 flex-1 overflow-hidden rounded-full bg-white/5'>
        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${pct}%` }} />
      </div>
      <span className='w-16 shrink-0 text-right font-mono text-dm-text-primary'>{formatCents(cents)}</span>
    </div>
  )
}

const MiniTrend = ({ months, values, selected, onPick, format }: {
  months: string[]
  values: number[]
  selected?: string | null
  onPick?: (month: string) => void
  format: (n: number) => string
}): JSX.Element => {
  const max = Math.max(...values, 1)
  return (
    <div className='flex h-10 items-end gap-0.5'>
      {months.map((m, i) => {
        const isSelected = selected === m
        const heightPct = Math.max(4, (values[i] / max) * 100)
        return (
          <button
            key={m}
            type='button'
            onClick={onPick !== undefined ? () => onPick(m) : undefined}
            disabled={onPick === undefined}
            title={`${monthLabel(m)}: ${format(values[i])}`}
            className={`flex h-full flex-1 items-end rounded-sm px-px ${onPick !== undefined ? 'cursor-pointer hover:bg-white/5' : 'cursor-default'}`}
          >
            <div
              className={`w-full rounded-sm ${isSelected ? 'bg-brand' : values[i] > 0 ? 'bg-brand/50' : 'bg-white/10'}`}
              style={{ height: `${heightPct}%` }}
            />
          </button>
        )
      })}
    </div>
  )
}

const billingLabelOf = (row: AccountUsageRow): string =>
  row.billingSource === 'shopify' ? 'Shopify' : row.billingSource === 'stripe' ? 'Stripe' : 'unknown'

const cancelLabelOf = (row: AccountUsageRow): string => {
  if (row.cancelAtPeriodEnd !== true) return 'no'
  return row.currentPeriodEnd != null
    ? `ends ${new Date(row.currentPeriodEnd).toLocaleDateString()}`
    : 'ends at period end'
}

const orderModeLabel = (page: AccountUsagePage | null): string | null => {
  if (page == null) return null
  if (page.orderMode === 'live') return 'live order; may shift as usage changes'
  if (page.orderMode === 'ranked') {
    return page.rankedAt != null ? `ranked as of ${formatLocalDateTime(page.rankedAt)}` : 'ranked order'
  }
  return null
}

const AccountsTableBody = ({ page, loading, error, onRetry, activeSearch, sort, onSortCost, onSortCreated, suspendBusyId, onOpen, onSuspend, deleteBusyId, deletingIds, scheduledIds, onDelete }: {
  page: AccountUsagePage | null
  loading: boolean
  error: string | null
  onRetry: () => void
  activeSearch: string | null
  sort: string
  onSortCost: () => void
  onSortCreated: () => void
  suspendBusyId: string | null
  onOpen: (row: AccountUsageRow) => void
  onSuspend: (row: AccountUsageRow, suspended: boolean) => void
  deleteBusyId: string | null
  deletingIds: Set<string>
  scheduledIds: Set<string>
  onDelete: (row: AccountUsageRow) => void
}): JSX.Element => {
  if (error !== null) return <CardError message={error} onRetry={onRetry} />
  if (loading && page == null) {
    return (
      <div className='flex items-center justify-center py-8'>
        <LoadingSpinner />
      </div>
    )
  }
  const accounts = page?.items ?? []
  if (accounts.length === 0) {
    return (
      <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary' data-testid='usage-accounts-empty'>
        {activeSearch != null
          ? 'No account matches. Search is an exact email or an exact account id.'
          : 'No accounts in this view yet.'}
      </p>
    )
  }
  const costDirection: SortDirection = sort === 'cost' ? 'descending' : 'none'
  const createdDirection: SortDirection = sort === 'newest' ? 'descending' : sort === 'oldest' ? 'ascending' : 'none'
  return (
    <div className='mt-3 overflow-x-auto'>
      <table className='w-full min-w-[1080px] text-left text-[11px]'>
        <thead>
          <tr className='border-b border-white/10 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
            <th className='py-1.5 pr-3'>Account</th>

            <SortHeader label='Cost (estimated)' direction={costDirection} onToggle={onSortCost} testid='usage-sort-cost' />
            <th className='py-1.5 pr-3' title='This month, as a share of the monthly YouTube unit budget'>YT quota share</th>
            <th className='py-1.5 pr-3'>Storage</th>
            <th className='py-1.5 pr-3' title='Lifetime net counts: streams, chat overlays, emote walls'>Entities</th>
            <th className='py-1.5 pr-3' title='Votes ingested this month'>Votes (month)</th>
            <th className='py-1.5 pr-3'>Plan</th>
            <th className='py-1.5 pr-3' title='Billing source: Stripe or Shopify'>Billing</th>
            <th className='py-1.5 pr-3' title='A scheduled plan cancellation and when the plan ends'>Cancel scheduled</th>
            <SortHeader label='Created' direction={createdDirection} onToggle={onSortCreated} testid='usage-sort-created' />
            <th className='py-1.5 pr-3'>Status</th>
            <th className='py-1.5'>Actions</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((row) => (
            <tr
              key={row.accountId}
              data-testid={`usage-account-row-${row.accountId}`}
              onClick={() => onOpen(row)}
              title='Open the 13-month drill-down for this account'
              className='cursor-pointer border-b border-white/5 text-dm-text-secondary hover:bg-white/5'
            >
              <td className='max-w-[14rem] truncate py-1.5 pr-3'><AccountLabel row={row} /></td>
              <td className='py-1.5 pr-3 font-mono text-dm-text-primary'>{formatEstimate(row.estimatedCents ?? 0)}</td>
              <td className='py-1.5 pr-3'>{(row.ytQuotaPctOfMonthlyBudget ?? 0).toFixed(1)}%</td>
              <td className='py-1.5 pr-3'>{formatBytes(row.storageBytes?.used ?? 0)}</td>
              <td className='py-1.5 pr-3' title='streams / chat overlays / emote walls'>
                {row.events?.used ?? 0} st · {row.chatOverlays?.used ?? 0} ch · {row.emoteWalls?.used ?? 0} ew
              </td>
              <td className='py-1.5 pr-3'>{counterValue(row.currentMonthUsage, 'votesIngested').toLocaleString()}</td>
              <td className='py-1.5 pr-3'>{row.tier}</td>
              <td className='py-1.5 pr-3' data-testid={`usage-billing-${row.accountId}`}>{billingLabelOf(row)}</td>
              <td className='py-1.5 pr-3' data-testid={`usage-cancel-${row.accountId}`}>{cancelLabelOf(row)}</td>
              <td className='py-1.5 pr-3'>
                {row.createdAt !== undefined ? new Date(row.createdAt).toLocaleDateString() : ''}
              </td>
              <td className='py-1.5 pr-3'>
                {suspendedOf(row) ? <SuspendedBadge /> : <span className='text-dm-text-accent'>active</span>}
              </td>
              <td className='py-1.5'>
                <div className='flex items-center gap-1.5'>
                  <SuspendButton row={row} busy={suspendBusyId === row.accountId} onSuspend={onSuspend} />
                  <DeleteButton
                    row={row}
                    busy={deleteBusyId === row.accountId}
                    inFlight={deletingIds.has(row.accountId)}
                    scheduled={scheduledIds.has(row.accountId)}
                    onDelete={onDelete}
                  />
                  <ChevronRightIcon className='size-3.5 text-dm-text-accent' aria-hidden='true' />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const AccountsCard = ({ page, loading, error, onRefresh, activeSearch, onSearch, sort, onSortCost, onSortCreated, filters, onFilter, filterNote, urlNotice, pageSize, onPageSize, pageNumber, onPrev, onNext, suspendBusyId, onOpen, onSuspend, deleteBusyId, deletingIds, scheduledIds, onDelete }: {
  page: AccountUsagePage | null
  loading: boolean
  error: string | null
  onRefresh: () => void
  activeSearch: string | null
  onSearch: (term: string | null) => void
  sort: string
  onSortCost: () => void
  onSortCreated: () => void
  filters: ToolbarChip[]
  onFilter: (key: string) => void
  filterNote: string | null
  urlNotice: string | null
  pageSize: number
  onPageSize: (size: number) => void
  pageNumber: number
  onPrev: () => void
  onNext: () => void
  suspendBusyId: string | null
  onOpen: (row: AccountUsageRow) => void
  onSuspend: (row: AccountUsageRow, suspended: boolean) => void
  deleteBusyId: string | null
  deletingIds: Set<string>
  scheduledIds: Set<string>
  onDelete: (row: AccountUsageRow) => void
}): JSX.Element => {
  const modeLabel = orderModeLabel(page)
  const hasNext = activeSearch == null && page?.nextToken != null
  const shortPage = (page?.items.length ?? 0) < pageSize
  return (
    <section
      data-testid='usage-accounts-card'
      className='rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <h2 className='text-base font-semibold text-dm-text-primary'>Accounts</h2>
          {page?.approxTotal != null && (
            <span className='inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-brand/15 px-2 text-xs font-semibold text-brand'>
              {page.approxTotal}
            </span>
          )}
        </div>
        <RefreshButton
          onClick={onRefresh}
          loading={loading}
          title='Refresh this page of the accounts table'
          testid='usage-accounts-refresh'
        />
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>
        Every account, most expensive first by default. Cost is a
        read-time floor estimate (current month counters plus live
        storage); click a row for the 13-month drill-down.
        {modeLabel != null && (
          <span className='ml-1 text-dm-text-accent' data-testid='usage-accounts-mode'>({modeLabel})</span>
        )}
      </p>
      <AdminListToolbar>
        <ChipRow label='Account filters' chips={filters} onChip={onFilter} testid='usage-filter' />
        <div className='flex flex-wrap items-center gap-3'>
          <SearchBox
            placeholder='Email or account id'
            label='Search accounts by exact email or account id'
            activeTerm={activeSearch}
            onSubmit={onSearch}
            testid='usage-search'
          />
          <PageSizePicker value={pageSize} onChange={onPageSize} testid='usage-size' />
        </div>
      </AdminListToolbar>
      {filterNote != null && (
        <p className='mt-2 text-xs text-amber-300' data-testid='usage-filter-note'>{filterNote}</p>
      )}
      {urlNotice != null && (
        <p className='mt-2 text-xs text-amber-300' data-testid='usage-url-notice'>{urlNotice}</p>
      )}
      <AccountsTableBody
        page={page}
        loading={loading}
        error={error}
        onRetry={onRefresh}
        activeSearch={activeSearch}
        sort={sort}
        onSortCost={onSortCost}
        onSortCreated={onSortCreated}
        suspendBusyId={suspendBusyId}
        onOpen={onOpen}
        onSuspend={onSuspend}
        deleteBusyId={deleteBusyId}
        deletingIds={deletingIds}
        scheduledIds={scheduledIds}
        onDelete={onDelete}
      />
      <CursorPager
        page={pageNumber}
        hasNext={hasNext}
        loading={loading}
        moreMayFollow={hasNext && shortPage}
        onPrev={onPrev}
        onNext={onNext}
        testid='usage-accounts-pager'
      />
    </section>
  )
}

const DrillDownBody = ({ row, history, loading, error, onRetry, selectedMonth, onSelectMonth }: {
  row: AccountUsageRow
  history: AccountUsageHistoryRow[] | null
  loading: boolean
  error: string | null
  onRetry: () => void
  selectedMonth: string
  onSelectMonth: (month: string) => void
}): JSX.Element => {
  if (error !== null) return <CardError message={error} onRetry={onRetry} />
  if (loading || history === null) {
    return (
      <div className='flex items-center justify-center py-8'>
        <LoadingSpinner />
      </div>
    )
  }

  const months = lastMonths(HISTORY_MONTHS)
  const byMonth = new Map(history.map((r): [string, AccountUsageHistoryRow] => [r.month, r]))
  const currentMonth = monthKeyOf(new Date())
  const selectedRec = byMonth.get(selectedMonth)
  const selectedCounters = selectedRec?.counters ?? null
  const driverRows = DRIVERS.map((d) => ({ driver: d, cents: driverCents(selectedCounters, d) }))
  const maxDriverCents = Math.max(...driverRows.map((r) => r.cents), 0)
  const costTrend = months.map((m) => byMonth.get(m)?.estimatedCents ?? 0)
  const quotaTrend = months.map((m) => quotaSharePct(counterValue(byMonth.get(m)?.counters, 'ytUnits'), m))

  return (
    <div className='mt-3 space-y-5'>

      <div className='flex flex-wrap gap-1.5' data-testid='usage-drilldown-months'>
        {months.map((m) => (
          <button
            key={m}
            type='button'
            onClick={() => onSelectMonth(m)}
            className={`rounded-md px-2 py-1 text-[11px] font-medium ${m === selectedMonth
              ? 'bg-brand/15 text-brand ring-1 ring-inset ring-brand/40'
              : 'border border-white/10 text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'}`}
          >
            {monthLabel(m)}
          </button>
        ))}
      </div>

      <div>
        <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
          {monthLabel(selectedMonth)} cost by driver (counters only)
        </h4>
        <p className='mt-1 text-[11px] text-dm-text-secondary'>
          Month estimate {formatCents(selectedRec?.estimatedCents ?? 0)}.
          Storage is a live number, not stored per month, so it is
          excluded from history months; the landing table&apos;s figure
          {selectedMonth === currentMonth ? ` (${formatEstimate(row.estimatedCents ?? 0)} this month)` : ''} includes it.
        </p>
        <div className='mt-2 space-y-1.5' data-testid='usage-drilldown-drivers'>
          {driverRows.map(({ driver, cents }) => (
            <BarRow key={driver.key} label={driver.label} cents={cents} maxCents={maxDriverCents} barClass={driver.barClass} />
          ))}
        </div>
      </div>

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        <div>
          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Cost trend (estimated, counters only)
          </h4>
          <MiniTrend months={months} values={costTrend} selected={selectedMonth} onPick={onSelectMonth} format={formatCents} />
        </div>
        <div>
          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            YouTube quota share trend (vs the {YT_DAILY_QUOTA_BUDGET_UNITS.toLocaleString()}/day default budget)
          </h4>
          <MiniTrend months={months} values={quotaTrend} selected={selectedMonth} onPick={onSelectMonth} format={(n) => `${n.toFixed(1)}%`} />
        </div>
      </div>

      <div>
        <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
          Counter trends (13 months)
        </h4>
        <div className='mt-2 grid grid-cols-1 gap-x-6 gap-y-3 lg:grid-cols-2' data-testid='usage-drilldown-counters'>
          {COUNTER_GROUPS.map((group) => {
            const values = months.map((m) => groupValue(byMonth.get(m)?.counters, group.counters))
            const format = group.format ?? ((n: number) => n.toLocaleString())
            return (
              <div key={group.label}>
                <div className='flex items-baseline justify-between text-[11px]'>
                  <span className='text-dm-text-secondary'>{group.label}</span>
                  <span className='font-mono text-dm-text-primary'>
                    {format(groupValue(selectedCounters, group.counters))}
                  </span>
                </div>
                <MiniTrend months={months} values={values} selected={selectedMonth} onPick={onSelectMonth} format={format} />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const DrillDownCard = ({ row, history, loading, error, onRetry, selectedMonth, onSelectMonth, onRefresh, onClose, suspendBusy, onSuspend }: {
  row: AccountUsageRow
  history: AccountUsageHistoryRow[] | null
  loading: boolean
  error: string | null
  onRetry: () => void
  selectedMonth: string
  onSelectMonth: (month: string) => void
  onRefresh: () => void
  onClose: () => void
  suspendBusy: boolean
  onSuspend: (row: AccountUsageRow, suspended: boolean) => void
}): JSX.Element => (
  <section
    data-testid='usage-drilldown-card'
    className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
  >
    <div className='flex items-center justify-between gap-3'>
      <div className='flex min-w-0 items-center gap-2'>
        <h2 className='shrink-0 text-base font-semibold text-dm-text-primary'>Drill-down</h2>
        <span className='min-w-0 truncate text-xs'><AccountLabel row={row} /></span>
        {suspendedOf(row) && <SuspendedBadge />}
      </div>
      <div className='flex shrink-0 items-center gap-2'>
        <SuspendButton row={row} busy={suspendBusy} onSuspend={onSuspend} />
        <RefreshButton
          onClick={onRefresh}
          loading={loading}
          title='Refresh the 13-month history for this account (one query)'
          testid='usage-drilldown-refresh'
        />
        <button
          type='button'
          onClick={onClose}
          title='Close the drill-down'
          data-testid='usage-drilldown-close'
          className='rounded-md border border-white/10 p-1.5 text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
        >
          <XMarkIcon className='size-3.5' aria-hidden='true' />
        </button>
      </div>
    </div>
    <p className='mt-1.5 text-xs text-dm-text-secondary'>
      Up to 13 months of this account&apos;s usage counters from one
      history query, cached until you leave the page. Historical cost
      is counters only (the honesty note below).
    </p>
    <DrillDownBody
      row={row}
      history={history}
      loading={loading}
      error={error}
      onRetry={onRetry}
      selectedMonth={selectedMonth}
      onSelectMonth={onSelectMonth}
    />
  </section>
)

interface StackPart {
  key: string
  barClass: string
  label: string
  cents: number
}

const fleetStackOf = (month: FleetMonth): StackPart[] =>
  DRIVERS.map((d) => ({
    key: d.key,
    barClass: d.barClass,
    label: d.label,
    cents: driverCents(month.counters, d)
  }))

const StackedBar = ({ parts, widthPct }: {
  parts: StackPart[]
  widthPct: number
}): JSX.Element => {
  const total = parts.reduce((s, p) => s + p.cents, 0)
  return (
    <div className='h-3 flex-1 overflow-hidden rounded-full bg-white/5'>
      <div className='flex h-full overflow-hidden rounded-full' style={{ width: `${widthPct}%` }}>
        {total > 0 && parts.map((p) => (
          <div
            key={p.key}
            className={`h-full ${p.barClass}`}
            style={{ width: `${(p.cents / total) * 100}%` }}
            title={`${p.label}: ${formatCents(p.cents)}`}
          />
        ))}
      </div>
    </div>
  )
}

const FleetCard = ({ months, loading, error, onRefresh }: {
  months: FleetMonth[] | null
  loading: boolean
  error: string | null
  onRefresh: () => void
}): JSX.Element => {
  const latest = months != null && months.length > 0 ? months[0] : null
  const maxTotal = months != null ? Math.max(...months.map((m) => m.estimatedCents ?? 0), 1) : 1
  return (
    <section
      data-testid='usage-fleet-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center justify-between gap-3'>
        <h2 className='text-base font-semibold text-dm-text-primary'>Fleet cost over time</h2>
        <RefreshButton
          onClick={onRefresh}
          loading={loading}
          title='Refresh the nightly fleet roll-up (one query)'
          testid='usage-fleet-refresh'
        />
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>
        The nightly roll-up: every account&apos;s counters summed per
        month, priced with the same counter rates (counters only, so no
        storage component). Figures are as of last night&apos;s run.
      </p>

      {error != null && <CardError message={error} onRetry={onRefresh} />}

      {error == null && (loading && months == null
        ? (
          <div className='flex items-center justify-center py-6'>
            <LoadingSpinner />
          </div>
          )
        : months == null || months.length === 0 || latest == null
          ? (
            <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary' data-testid='usage-fleet-empty'>
              The nightly roll-up has not run yet.
            </p>
            )
          : (
            <>

              <div className='mt-3' data-testid='usage-fleet-current'>
                <div className='flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm text-dm-text-primary'>
                  <span>
                    {monthLabel(latest.month)} total{' '}
                    <span className='font-semibold'>{formatEstimate(latest.estimatedCents ?? 0)}</span>{' '}
                    <span className='text-xs text-dm-text-accent'>(estimated, counters only)</span>
                  </span>
                  <span>
                    Average per account{' '}
                    <span className='font-semibold'>
                      {formatCents(latest.accountCount > 0 ? (latest.estimatedCents ?? 0) / latest.accountCount : 0)}
                    </span>
                  </span>
                  <span className='text-xs text-dm-text-accent'>
                    {latest.accountCount} accounts, {latest.accountsWithUsage} with usage
                  </span>
                </div>
                <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='usage-fleet-asof'>
                  as of last night ({formatLocalDateTime(latest.rolledUpAt)})
                </p>
              </div>

              <div className='mt-4 space-y-1.5' data-testid='usage-fleet-trend'>
                {months.map((m) => (
                  <div key={m.month} className='flex items-center gap-2 text-[11px]'>
                    <span className='w-16 shrink-0 font-mono text-dm-text-secondary'>{monthLabel(m.month)}</span>
                    <StackedBar parts={fleetStackOf(m)} widthPct={Math.max(1, ((m.estimatedCents ?? 0) / maxTotal) * 100)} />
                    <span className='w-16 shrink-0 text-right font-mono text-dm-text-primary'>{formatCents(m.estimatedCents ?? 0)}</span>
                    <span className='w-24 shrink-0 text-right text-dm-text-accent'>
                      avg {formatCents(m.accountCount > 0 ? (m.estimatedCents ?? 0) / m.accountCount : 0)}
                    </span>
                  </div>
                ))}
                <p className='mt-1.5 text-[11px] text-dm-text-accent'>
                  Month totals are counters only (storage is live, never
                  stored per month). The stack splits each month by
                  driver from the same counter rates.
                </p>
              </div>
            </>
            ))}
    </section>
  )
}

const OutliersBody = ({ page, loading, error, onRetry, suspendBusyId, onOpen, onSuspend }: {
  page: AccountUsagePage | null
  loading: boolean
  error: string | null
  onRetry: () => void
  suspendBusyId: string | null
  onOpen: (row: AccountUsageRow) => void
  onSuspend: (row: AccountUsageRow, suspended: boolean) => void
}): JSX.Element => {
  if (error !== null) return <CardError message={error} onRetry={onRetry} />
  if (loading && page == null) {
    return (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  }
  const outliers = page?.items ?? []
  if (outliers.length === 0) {
    return (
      <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary' data-testid='usage-outliers-empty'>
        No accounts over the threshold this month.
      </p>
    )
  }
  return (
    <ul className='mt-3 space-y-1.5'>
      {outliers.map((row) => (
        <li
          key={row.accountId}
          data-testid={`usage-outlier-row-${row.accountId}`}
          className='flex flex-wrap items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2'
        >
          <button
            type='button'
            onClick={() => onOpen(row)}
            title='Open the drill-down for this account'
            className='min-w-0 flex-1 truncate text-left text-xs hover:underline'
          >
            <AccountLabel row={row} />
          </button>
          <span className='shrink-0 font-mono text-xs text-dm-text-primary'>{formatEstimate(row.estimatedCents ?? 0)}</span>
          <span className='shrink-0 text-[11px] text-dm-text-accent'>{(row.ytQuotaPctOfMonthlyBudget ?? 0).toFixed(1)}% YT</span>
          {suspendedOf(row)
            ? <SuspendedBadge />
            : <SuspendButton row={row} busy={suspendBusyId === row.accountId} onSuspend={onSuspend} />}
        </li>
      ))}
    </ul>
  )
}

const OutliersCard = ({ page, loading, error, onRefresh, pageSize, onPageSize, pageNumber, onPrev, onNext, suspendBusyId, onOpen, onSuspend }: {
  page: AccountUsagePage | null
  loading: boolean
  error: string | null
  onRefresh: () => void
  pageSize: number
  onPageSize: (size: number) => void
  pageNumber: number
  onPrev: () => void
  onNext: () => void
  suspendBusyId: string | null
  onOpen: (row: AccountUsageRow) => void
  onSuspend: (row: AccountUsageRow, suspended: boolean) => void
}): JSX.Element => {
  const modeLabel = orderModeLabel(page)
  return (
    <section
      data-testid='usage-outliers-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <ExclamationTriangleIcon className='size-5 text-amber-300' aria-hidden='true' />
          <h2 className='text-base font-semibold text-dm-text-primary'>Outliers</h2>
        </div>
        <div className='flex items-center gap-3'>
          <PageSizePicker value={pageSize} onChange={onPageSize} testid='usage-outliers-size' />
          <RefreshButton
            onClick={onRefresh}
            loading={loading}
            title='Refresh this page of outliers (its own query)'
            testid='usage-outliers-refresh'
          />
        </div>
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>
        Accounts over the outlier threshold this month (
        {formatEstimate(OUTLIER_THRESHOLD_CENTS)} by default, the
        nightly job&apos;s knob). The nightly check runs at 02:43 UTC
        against the same threshold and pages the ops topic on crossings;
        figures here are the same read-time floor estimates.
        {modeLabel != null && (
          <span className='ml-1 text-dm-text-accent' data-testid='usage-outliers-mode'>({modeLabel})</span>
        )}
      </p>
      <OutliersBody
        page={page}
        loading={loading}
        error={error}
        onRetry={onRefresh}
        suspendBusyId={suspendBusyId}
        onOpen={onOpen}
        onSuspend={onSuspend}
      />
      <CursorPager
        page={pageNumber}
        hasNext={page?.nextToken != null}
        loading={loading}
        onPrev={onPrev}
        onNext={onNext}
        testid='usage-outliers-pager'
      />
    </section>
  )
}

const DialogHeading = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <h3 className='mt-4 text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
    {children}
  </h3>
)

const DeleteAccountDialog = ({ row, preview, previewLoading, previewError, busy, error, onConfirm, onClose }: {
  row: AccountUsageRow
  preview: AccountDeletionPreview | null
  previewLoading: boolean
  previewError: boolean
  busy: boolean
  error: string | null
  onConfirm: (suspendedReviewed: boolean) => void
  onClose: () => void
}): JSX.Element => {
  const [typed, setTyped] = useState('')
  const [reviewed, setReviewed] = useState(false)
  const suspended = suspendedOf(row) || preview?.suspended === true
  const who = emailOf(row) ?? 'this account'
  const copy = preview !== null ? deleteDialogCopy(preview, who, formatStamp(preview.scheduledRunAt)) : null
  const showSchedule = copy !== null && copy.alreadyPending === null
  const canConfirm = showSchedule && copy.blocked === null && typed === DELETE_CONFIRM_WORD && (!suspended || reviewed) && !busy
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
      data-testid='usage-delete-dialog'
      role='dialog'
      aria-modal='true'
      aria-labelledby='usage-delete-dialog-title'
    >
      <div className='max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-white/10 bg-dm-bg-secondary p-6 shadow-2xl'>
        <h2 id='usage-delete-dialog-title' className='flex items-center gap-2 text-lg font-semibold text-dm-text-primary'>
          <TrashIcon className='size-5 text-red-300' aria-hidden='true' />
          {`Are you sure you want to delete ${who}?`}
        </h2>

        {copy === null && (
          <p className='mt-3 flex items-center gap-2 text-sm text-dm-text-secondary' data-testid='usage-delete-preview-state'>
            {previewLoading && !previewError && <LoadingSpinner />}
            {previewError ? DIALOG_PREVIEW_ERROR : DIALOG_LOADING}
          </p>
        )}

        {copy !== null && (
          <>
            <p className='mt-2 text-sm text-dm-text-secondary'>{copy.lead}</p>

            {copy.alreadyPending !== null && (
              <p
                data-testid='usage-delete-already-pending'
                className='mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200'
              >
                {copy.alreadyPending}
              </p>
            )}

            {copy.staffCallout.length > 0 && (
              <div
                data-testid='usage-delete-staff-note'
                className='mt-4 space-y-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200'
              >
                {copy.staffCallout.map((line) => <p key={line}>{line}</p>)}
              </div>
            )}

            {copy.liveNote !== null && (
              <p data-testid='usage-delete-live-note' className='mt-3 text-xs text-amber-300'>
                {copy.liveNote}
              </p>
            )}

            {copy.blocked !== null && (
              <div
                data-testid='usage-delete-blocked'
                className='mt-4 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200'
              >
                <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
                <span>{copy.blocked}</span>
              </div>
            )}

            {suspended && (
              <div
                data-testid='usage-delete-suspended-note'
                className='mt-4 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200'
              >
                <ShieldExclamationIcon className='size-4 shrink-0' aria-hidden='true' />
                <span>
                  This account is suspended. Banned accounts are only deleted
                  when the person asks, and only after a legal review of
                  whether we must keep a minimal record so they cannot rejoin.
                  Do not continue until that review is done.
                </span>
              </div>
            )}

            <DialogHeading>What is deleted</DialogHeading>
            <ul className='mt-2 list-disc space-y-1.5 pl-5 text-sm text-dm-text-secondary'>
              {copy.deletedItems.map((item) => <li key={item}>{item}</li>)}
            </ul>

            <DialogHeading>What is kept</DialogHeading>
            <ul className='mt-2 list-disc space-y-1.5 pl-5 text-sm text-dm-text-secondary'>
              {copy.keptItems.map((item) => <li key={item}>{item}</li>)}
            </ul>

            {showSchedule && (
              <label className='mt-5 block text-sm text-dm-text-primary'>
                Type <span className='font-mono font-semibold'>{DELETE_CONFIRM_WORD}</span> to confirm
                <input
                  type='text'
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  disabled={busy}
                  autoComplete='off'
                  spellCheck={false}
                  placeholder={DELETE_CONFIRM_WORD}
                  data-testid='usage-delete-confirm-input'
                  className='mt-1.5 block w-full rounded-md border border-white/10 bg-dm-bg-accent/60 px-3 py-2 font-mono text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none'
                />
              </label>
            )}

            {showSchedule && suspended && (
              <label className='mt-4 flex cursor-pointer items-start gap-2.5 rounded-md border border-white/10 bg-dm-bg-accent/30 p-3 text-sm text-dm-text-primary'>
                <input
                  type='checkbox'
                  checked={reviewed}
                  onChange={(e) => setReviewed(e.target.checked)}
                  disabled={busy}
                  data-testid='usage-delete-legal-checkbox'
                  className='mt-0.5 size-4 shrink-0 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                />
                <span>Legal review is done for deleting a banned account</span>
              </label>
            )}
          </>
        )}

        {error !== null && (
          <div className='mt-4' data-testid='usage-delete-error'>
            <ErrorInline>{error}</ErrorInline>
          </div>
        )}

        <div className='mt-5 flex items-center justify-end gap-2'>
          <button
            type='button'
            onClick={onClose}
            disabled={busy}
            data-testid='usage-delete-cancel'
            className='rounded-md border border-white/10 px-4 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            Close
          </button>
          {(copy === null || showSchedule) && (
            <button
              type='button'
              onClick={() => onConfirm(suspended && reviewed)}
              disabled={!canConfirm}
              data-testid='usage-delete-confirm'
              className='inline-flex items-center gap-1.5 rounded-md bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busy && <LoadingSpinner />}
              {busy ? (copy?.busyLabel ?? 'Scheduling') : (copy?.confirmLabel ?? 'Schedule deletion')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const DeletionStatusBadge = ({ status }: { status: string }): JSX.Element => {
  const tone = status === 'COMPLETE'
    ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30'
    : status === 'FAILED' || status === 'STOPPED'
      ? 'bg-red-500/15 text-red-300 ring-red-500/30'
      : DELETION_IN_FLIGHT_STATUSES.has(status) || status === 'SCHEDULED'
        ? 'bg-amber-500/15 text-amber-300 ring-amber-500/30'
        : 'bg-white/5 text-dm-text-secondary ring-white/10'
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ${tone}`}>
      {deletionStatusLabel(status)}
    </span>
  )
}

const DeletionLabel = ({ row }: { row: AccountDeletionRow }): JSX.Element => {
  if (!row.accountExists) {
    return (
      <span
        className='italic text-dm-text-accent'
        title='The account row no longer exists; deletion records hold no email by design'
      >
        account removed
      </span>
    )
  }
  if (row.email == null) {
    return (
      <span
        className='italic text-dm-text-accent'
        title='Email is stored when the account next logs in; this row is not resolved yet'
      >
        email not resolved
      </span>
    )
  }
  return <span className='truncate text-dm-text-primary'>{row.email}</span>
}

const DeletionsBody = ({ page, loading, error, onRetryLoad, retryBusyId, onRetryDeletion }: {
  page: AccountDeletionPage | null
  loading: boolean
  error: string | null
  onRetryLoad: () => void
  retryBusyId: string | null
  onRetryDeletion: (deletion: AccountDeletionRow) => void
}): JSX.Element | null => {
  if (error !== null) return <CardError message={error} onRetry={onRetryLoad} />
  if (page == null) {
    if (!loading) return null
    return (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  }
  const deletions = page.items
  if (deletions.length === 0) {
    return (
      <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary' data-testid='usage-deletions-empty'>
        No deletion requests in this view.
      </p>
    )
  }
  return (
    <div className='mt-3 overflow-x-auto'>
      <table className='w-full min-w-[760px] text-left text-[11px]' data-testid='usage-deletions-table'>
        <thead>
          <tr className='border-b border-white/10 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
            <th className='py-1.5 pr-3'>Account</th>
            <th className='py-1.5 pr-3'>Status</th>
            <th className='py-1.5 pr-3'>Requested</th>
            <th className='py-1.5 pr-3'>Completed</th>
            <th className='py-1.5 pr-3' title='The account was suspended (banned) when deletion was requested'>Was suspended</th>
            <th className='py-1.5 pr-3'>Failed steps</th>
            <th className='py-1.5'>Actions</th>
          </tr>
        </thead>
        <tbody>
          {deletions.map((d, i) => {
            const failed = failedStepsOf(d)
            const note = deletionRowNote(d)
            return (
              <tr
                key={d.accountId !== '' ? d.accountId : `deletion-${i}`}
                data-testid={`usage-deletion-row-${i}`}
                className='border-b border-white/5 align-top text-dm-text-secondary'
              >
                <td className='max-w-[14rem] truncate py-1.5 pr-3'><DeletionLabel row={d} /></td>
                <td className='py-1.5 pr-3'>
                  <DeletionStatusBadge status={d.status} />
                  {d.needsAction && (
                    <span
                      data-testid={`usage-deletion-needs-action-${i}`}
                      className='ml-1.5 inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300 ring-1 ring-inset ring-amber-500/30'
                    >
                      needs action
                    </span>
                  )}
                  {note !== null && (
                    <p data-testid={`usage-deletion-note-${i}`} className='mt-1 max-w-[16rem] text-[10px] text-amber-300'>
                      {note}
                    </p>
                  )}
                </td>
                <td className='py-1.5 pr-3'>{formatStamp(d.scheduledAt ?? d.requestedAt)}</td>
                <td className='py-1.5 pr-3'>{formatStamp(d.completedAt) !== '' ? formatStamp(d.completedAt) : <span className='text-dm-text-accent'>not yet</span>}</td>
                <td className='py-1.5 pr-3'>{d.wasSuspended ? <SuspendedBadge /> : <span className='text-dm-text-accent'>no</span>}</td>
                <td className='py-1.5 pr-3'>
                  {failed.length === 0
                    ? <span className='text-dm-text-accent'>none</span>
                    : (
                      <ul className='space-y-0.5' data-testid={`usage-deletion-failed-steps-${i}`}>
                        {failed.map((f) => (
                          <li key={f.name} className='text-red-300'>
                            {f.name}
                            {f.reason !== null && <span className='ml-1 font-mono text-[10px] text-red-300/80'>({f.reason})</span>}
                          </li>
                        ))}
                      </ul>
                      )}
                </td>
                <td className='py-1.5'>

                  {d.needsAction && (
                    <button
                      type='button'
                      onClick={() => onRetryDeletion(d)}
                      disabled={retryBusyId !== null}
                      title='Run the deletion again; every step is safe to repeat'
                      data-testid={`usage-deletion-retry-${i}`}
                      className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      {retryBusyId === d.accountId ? <LoadingSpinner /> : <ArrowPathIcon className='size-3' aria-hidden='true' />}
                      Retry
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const DELETION_VIEWS = ['needs_action', 'in_progress', 'complete', 'all'] as const

const DELETION_VIEW_LABELS: Record<string, string> = {
  needs_action: 'Needs action',
  in_progress: 'In progress',
  complete: 'Complete',
  all: 'All'
}

const DeletionsCard = ({ page, view, onView, loading, error, loaded, onLoad, pageSize, onPageSize, pageNumber, onPrev, onNext, retryBusyId, onRetryDeletion }: {
  page: AccountDeletionPage | null
  view: string
  onView: (view: string) => void
  loading: boolean
  error: string | null
  loaded: boolean
  onLoad: () => void
  pageSize: number
  onPageSize: (size: number) => void
  pageNumber: number
  onPrev: () => void
  onNext: () => void
  retryBusyId: string | null
  onRetryDeletion: (deletion: AccountDeletionRow) => void
}): JSX.Element => (
  <section
    data-testid='usage-deletions-card'
    className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
  >
    <div className='flex items-center justify-between gap-3'>
      <div className='flex items-center gap-2'>
        <TrashIcon className='size-5 text-red-300' aria-hidden='true' />
        <h2 className='text-base font-semibold text-dm-text-primary'>Deletion requests</h2>
      </div>
      <div className='flex items-center gap-3'>
        {loaded && <PageSizePicker value={pageSize} onChange={onPageSize} testid='usage-deletions-size' />}
        <button
          onClick={onLoad}
          disabled={loading}
          title='Load the account deletion records (one page per query)'
          data-testid='usage-deletions-load'
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          <ArrowPathIcon className={`size-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden='true' />
          {!loaded ? 'Load deletion requests' : 'Refresh'}
        </button>
      </div>
    </div>
    <p className='mt-1.5 text-xs text-dm-text-secondary'>
      {DELETIONS_CARD_HELP}
    </p>
    {loaded && (
      <div className='mt-3'>
        <ChipRow
          label='Deletion views'
          chips={DELETION_VIEWS.map((v) => ({ key: v, label: DELETION_VIEW_LABELS[v], active: view === v }))}
          onChip={onView}
          testid='usage-deletions-chip'
        />
      </div>
    )}
    <DeletionsBody
      page={page}
      loading={loading}
      error={error}
      onRetryLoad={onLoad}
      retryBusyId={retryBusyId}
      onRetryDeletion={onRetryDeletion}
    />
    {loaded && (
      <CursorPager
        page={pageNumber}
        hasNext={page?.nextToken != null}
        loading={loading}
        moreMayFollow={page?.nextToken != null && (page?.items.length ?? 0) < pageSize && view !== 'all'}
        onPrev={onPrev}
        onNext={onNext}
        testid='usage-deletions-pager'
      />
    )}
  </section>
)

const ScheduledLabel = ({ row }: { row: ScheduledAccountDeletion }): JSX.Element => {
  if (!row.accountExists) {
    return (
      <span className='italic text-dm-text-accent' title='The account row no longer exists'>
        account removed
      </span>
    )
  }
  if (row.email == null) {
    return (
      <span className='italic text-dm-text-accent' title='Email is stored when the account next logs in; this row is not resolved yet'>
        email not resolved
      </span>
    )
  }
  return <span className='truncate text-dm-text-primary'>{row.email}</span>
}

const ScheduledDeletionsCard = ({ rows, loading, error, onRefresh, cancelBusyId, onCancel }: {
  rows: ScheduledAccountDeletion[] | null
  loading: boolean
  error: string | null
  onRefresh: () => void
  cancelBusyId: string | null
  onCancel: (row: ScheduledAccountDeletion) => void
}): JSX.Element => (
  <section
    data-testid='usage-scheduled-deletions-card'
    className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
  >
    <div className='flex items-center justify-between gap-3'>
      <div className='flex items-center gap-2'>
        <ClockIcon className='size-5 text-amber-300' aria-hidden='true' />
        <h2 className='text-base font-semibold text-dm-text-primary'>{SCHEDULED_CARD_HEADING}</h2>
      </div>
      <RefreshButton
        onClick={onRefresh}
        loading={loading}
        title='Refresh the scheduled deletions'
        testid='usage-scheduled-deletions-refresh'
      />
    </div>
    <p className='mt-1.5 text-xs text-dm-text-secondary'>{SCHEDULED_CARD_HELP}</p>
    {error !== null && <CardError message={error} onRetry={onRefresh} />}
    {error === null && rows === null && loading && (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )}
    {error === null && rows !== null && rows.length === 0 && (
      <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary' data-testid='usage-scheduled-deletions-empty'>
        {SCHEDULED_CARD_EMPTY}
      </p>
    )}
    {error === null && rows !== null && rows.length > 0 && (
      <div className='mt-3 overflow-x-auto'>
        <table className='w-full min-w-[760px] text-left text-[11px]' data-testid='usage-scheduled-deletions-table'>
          <thead>
            <tr className='border-b border-white/10 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
              <th className='py-1.5 pr-3'>Account</th>
              <th className='py-1.5 pr-3'>Kind</th>
              <th className='py-1.5 pr-3'>Scheduled by</th>
              <th className='py-1.5 pr-3'>Starts after</th>
              <th className='py-1.5 pr-3'>Status</th>
              <th className='py-1.5'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.accountId !== '' ? r.accountId : `scheduled-${i}`}
                data-testid={`usage-scheduled-deletion-row-${i}`}
                className='border-b border-white/5 align-top text-dm-text-secondary'
              >
                <td className='max-w-[14rem] truncate py-1.5 pr-3'><ScheduledLabel row={r} /></td>
                <td className='py-1.5 pr-3'>{kindLabel(r.staffAccount)}</td>
                <td className='py-1.5 pr-3'>{scheduledByLabel(r.requestedByMe)}</td>
                <td className='py-1.5 pr-3'>{formatStamp(r.runAt)}</td>
                <td className='max-w-[22rem] py-1.5 pr-3'>
                  <DeletionStatusBadge status={r.status} />
                  {(r.status === 'STOPPED' || r.heldReason != null) && (
                    <p data-testid={`usage-scheduled-deletion-status-${i}`} className={r.status === 'STOPPED' ? 'mt-1 text-[10px] text-red-300' : 'mt-1 text-[10px] text-amber-300'}>
                      {scheduledStatusCopy(r)}
                    </p>
                  )}
                </td>
                <td className='py-1.5'>
                  {r.status === 'SCHEDULED' && (
                    <button
                      type='button'
                      onClick={() => onCancel(r)}
                      disabled={cancelBusyId !== null}
                      title='Cancel this scheduled deletion (a confirm step follows)'
                      data-testid={`usage-scheduled-deletion-cancel-${i}`}
                      className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      {cancelBusyId === r.accountId ? <LoadingSpinner /> : <XMarkIcon className='size-3' aria-hidden='true' />}
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
)

export const UsagePage = (): JSX.Element | null => {
  const { auth0Token, streamToolsUser } = useAuth()
  const navigate = useNavigate()
  const { superuser } = useEntitlements()
  useEffect(() => {
    if (streamToolsUser !== null && !superuser) {
      void navigate('/', { replace: true })
    }
  }, [streamToolsUser, superuser, navigate])

  const [searchParams, setSearchParams] = useSearchParams()
  const sortParsed = parseChoice(searchParams.get('sort'), ['cost', 'newest', 'oldest'], 'cost', 'Sort')
  const sizeParsed = parsePageSize(searchParams.get('size'), 50)
  const planParsed = parseChoice(searchParams.get('plan'), ['free', 'creator', 'professional'], '', 'Plan')
  const youtubeParsed = parseChoice(searchParams.get('youtube'), ['waitlisted', 'invited', 'demo'], '', 'YouTube filter')
  const outlierSizeParsed = parsePageSize(searchParams.get('osize'), 25)
  const deletionViewParsed = parseChoice(searchParams.get('dview'), DELETION_VIEWS, 'needs_action', 'Deletions view')
  const deletionSizeParsed = parsePageSize(searchParams.get('dsize'), 25)
  const sort = sortParsed.value
  const accountsPageSize = sizeParsed.value
  const planFilter = planParsed.value
  const youtubeFilter = youtubeParsed.value
  const paidFilter = parseFlag(searchParams.get('paid'))
  const suspendedFilter = parseFlag(searchParams.get('suspended'))
  const outliersPageSize = outlierSizeParsed.value
  const deletionsView = deletionViewParsed.value
  const deletionsPageSize = deletionSizeParsed.value
  const accountsUrlNotice = sortParsed.notice ?? sizeParsed.notice ?? planParsed.notice ?? youtubeParsed.notice

  const setParam = useCallback((key: string, value: string | null): void => {
    const next = new URLSearchParams(globalThis.location.search)
    if (value == null) next.delete(key)
    else next.set(key, value)
    setSearchParams(next)
  }, [setSearchParams])

  const [accountsPage, setAccountsPage] = useState<AccountUsagePage | null>(null)
  const [accountsTokens, setAccountsTokens] = useState<string[]>(resetTokens())
  const [accountsSearch, setAccountsSearch] = useState<string | null>(null)
  const [accountsLoading, setAccountsLoading] = useState(true)
  const [accountsError, setAccountsError] = useState<string | null>(null)
  const [rankedFilterNote, setRankedFilterNote] = useState<string | null>(null)
  const accountsRaceRef = useRef(createRequestRace())
  const accountsGateRef = useRef(createPagerGate())
  const accountsListKey = `${accountsPageSize}|${sort}|${planFilter}|${String(paidFilter)}|${String(suspendedFilter)}|${youtubeFilter}`
  const [accountsTokensKey, setAccountsTokensKey] = useState(accountsListKey)
  if (accountsTokensKey !== accountsListKey) {
    setAccountsTokensKey(accountsListKey)
    setAccountsTokens(resetTokens())
  }
  useLayoutEffect(() => {
    accountsGateRef.current.drop()
  }, [accountsListKey])

  const [selected, setSelected] = useState<AccountUsageRow | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<string>(() => monthKeyOf(new Date()))
  const historyCacheRef = useRef<Map<string, AccountUsageHistoryRow[]>>(new Map())
  const [history, setHistory] = useState<AccountUsageHistoryRow[] | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)

  const [outliersPage, setOutliersPage] = useState<AccountUsagePage | null>(null)
  const [outliersTokens, setOutliersTokens] = useState<string[]>(resetTokens())
  const [outliersLoading, setOutliersLoading] = useState(true)
  const [outliersError, setOutliersError] = useState<string | null>(null)
  const outliersRaceRef = useRef(createRequestRace())
  const outliersGateRef = useRef(createPagerGate())
  const outliersListKey = String(outliersPageSize)
  const [outliersTokensKey, setOutliersTokensKey] = useState(outliersListKey)
  if (outliersTokensKey !== outliersListKey) {
    setOutliersTokensKey(outliersListKey)
    setOutliersTokens(resetTokens())
  }
  useLayoutEffect(() => {
    outliersGateRef.current.drop()
  }, [outliersListKey])

  const [fleetMonths, setFleetMonths] = useState<FleetMonth[] | null>(null)
  const [fleetLoading, setFleetLoading] = useState(true)
  const [fleetError, setFleetError] = useState<string | null>(null)

  const [suspendBusyId, setSuspendBusyId] = useState<string | null>(null)
  const [suspendNotice, setSuspendNotice] = useState<{ text: string, suspended: boolean } | null>(null)
  const [suspendError, setSuspendError] = useState<string | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<AccountUsageRow | null>(null)
  const [deleteBusyId, setDeleteBusyId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteNotice, setDeleteNotice] = useState<string | null>(null)
  const [deletionsPage, setDeletionsPage] = useState<AccountDeletionPage | null>(null)
  const [deletionsTokens, setDeletionsTokens] = useState<string[]>(resetTokens())
  const [deletionsLoaded, setDeletionsLoaded] = useState(false)
  const [deletionsLoading, setDeletionsLoading] = useState(false)
  const [deletionsError, setDeletionsError] = useState<string | null>(null)
  const deletionsRaceRef = useRef(createRequestRace())
  const deletionsGateRef = useRef(createPagerGate())
  const deletionsListKey = `${deletionsView}|${deletionsPageSize}`
  const [deletionsTokensKey, setDeletionsTokensKey] = useState(deletionsListKey)
  if (deletionsTokensKey !== deletionsListKey) {
    setDeletionsTokensKey(deletionsListKey)
    setDeletionsTokens(resetTokens())
  }
  useLayoutEffect(() => {
    deletionsGateRef.current.drop()
  }, [deletionsListKey])
  const [retryBusyId, setRetryBusyId] = useState<string | null>(null)
  const [retryError, setRetryError] = useState<string | null>(null)

  const [deletePreview, setDeletePreview] = useState<AccountDeletionPreview | null>(null)
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false)
  const [deletePreviewError, setDeletePreviewError] = useState(false)
  const previewRaceRef = useRef(createRequestRace())
  const [scheduledRows, setScheduledRows] = useState<ScheduledAccountDeletion[] | null>(null)
  const [scheduledLoading, setScheduledLoading] = useState(false)
  const [scheduledError, setScheduledError] = useState<string | null>(null)
  const scheduledRaceRef = useRef(createRequestRace())
  const [cancelBusyId, setCancelBusyId] = useState<string | null>(null)
  const [cancelNotice, setCancelNotice] = useState<string | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)

  const loadAccounts = useCallback(async (stack: string[], search: string | null): Promise<void> => {
    if (auth0Token === undefined) return
    const requestId = accountsRaceRef.current.begin()
    accountsGateRef.current.hold()
    setAccountsLoading(true)
    setAccountsError(null)
    try {
      const variables = search != null
        ? { limit: accountsPageSize, search }
        : {
            limit: accountsPageSize,
            nextToken: tokenForPage(stack),
            sort,
            plan: planFilter === '' ? null : planFilter,
            paid: paidFilter ? true : null,
            suspended: suspendedFilter ? true : null,
            youtube: youtubeFilter === '' ? null : youtubeFilter
          }
      const { page: fetched } = await graphqlRequest<{ page: AccountUsagePage }>(
        listAccountUsagePageQuery, variables, auth0Token
      )
      if (!accountsRaceRef.current.isCurrent(requestId)) return
      setAccountsPage(fetched)
      accountsGateRef.current.settle(search != null ? null : fetched.nextToken)
    } catch (err) {
      if (!accountsRaceRef.current.isCurrent(requestId)) return
      accountsGateRef.current.fail()
      setAccountsError(err instanceof Error ? err.message : 'Failed to load account usage')
    } finally {
      if (accountsRaceRef.current.isCurrent(requestId)) setAccountsLoading(false)
    }
  }, [auth0Token, accountsPageSize, sort, planFilter, paidFilter, suspendedFilter, youtubeFilter])

  useEffect(() => {
    void loadAccounts(accountsTokens, accountsSearch)
  }, [loadAccounts, accountsTokens, accountsSearch])

  const onAccountsSearch = (term: string | null): void => {
    accountsGateRef.current.drop()
    setAccountsTokens(resetTokens())
    setAccountsLoading(true)
    setAccountsSearch(term)
  }

  const onAccountsPageSize = (size: number): void => {
    accountsGateRef.current.drop()
    setAccountsTokens(resetTokens())
    setAccountsLoading(true)
    setParam('size', String(size))
  }

  const onSortCost = (): void => {
    accountsGateRef.current.drop()
    setRankedFilterNote(null)
    setAccountsSearch(null)
    setAccountsTokens(resetTokens())
    setAccountsLoading(true)
    setParam('sort', 'cost')
  }

  const onSortCreated = (): void => {
    accountsGateRef.current.drop()
    setRankedFilterNote(null)
    setAccountsSearch(null)
    setAccountsTokens(resetTokens())
    setAccountsLoading(true)
    setParam('sort', sort === 'newest' ? 'oldest' : 'newest')
  }

  const onFilter = (key: string): void => {
    const flipToNewest = sort === 'cost' && accountsPage?.orderMode === 'ranked'
    if (flipToNewest) {
      setRankedFilterNote('Filters combine with the newest or oldest order while the list is nightly-ranked; sorted by newest.')
    }
    accountsGateRef.current.drop()
    setAccountsSearch(null)
    setAccountsTokens(resetTokens())
    setAccountsLoading(true)
    const next = new URLSearchParams(globalThis.location.search)
    const toggle = (name: string, value: string): void => {
      if (next.get(name) === value) next.delete(name)
      else next.set(name, value)
    }
    if (key === 'free' || key === 'creator' || key === 'professional') toggle('plan', key)
    else if (key === 'paid') toggle('paid', '1')
    else if (key === 'suspended') toggle('suspended', '1')
    else toggle('youtube', key)
    if (flipToNewest) next.set('sort', 'newest')
    setSearchParams(next)
  }

  const accountsFilterChips: ToolbarChip[] = [
    { key: 'free', label: 'Free', active: planFilter === 'free' },
    { key: 'creator', label: 'Creator', active: planFilter === 'creator' },
    { key: 'professional', label: 'Professional', active: planFilter === 'professional' },
    { key: 'paid', label: 'Paid', active: paidFilter },
    { key: 'suspended', label: 'Suspended', active: suspendedFilter },
    { key: 'waitlisted', label: 'YT waitlisted', active: youtubeFilter === 'waitlisted' },
    { key: 'invited', label: 'YT invited', active: youtubeFilter === 'invited' },
    { key: 'demo', label: 'YT demo', active: youtubeFilter === 'demo' }
  ]

  const loadOutliers = useCallback(async (stack: string[]): Promise<void> => {
    if (auth0Token == null) return
    const requestId = outliersRaceRef.current.begin()
    outliersGateRef.current.hold()
    setOutliersLoading(true)
    setOutliersError(null)
    try {
      const { page: fetched } = await graphqlRequest<{ page: AccountUsagePage }>(
        listUsageOutliersQuery, { limit: outliersPageSize, nextToken: tokenForPage(stack) }, auth0Token
      )
      if (!outliersRaceRef.current.isCurrent(requestId)) return
      setOutliersPage(fetched)
      outliersGateRef.current.settle(fetched.nextToken)
    } catch (err) {
      if (!outliersRaceRef.current.isCurrent(requestId)) return
      outliersGateRef.current.fail()
      setOutliersError(err instanceof Error ? err.message : 'Failed to load outliers')
    } finally {
      if (outliersRaceRef.current.isCurrent(requestId)) setOutliersLoading(false)
    }
  }, [auth0Token, outliersPageSize])

  useEffect(() => {
    void loadOutliers(outliersTokens)
  }, [loadOutliers, outliersTokens])

  const loadFleet = useCallback(async (): Promise<void> => {
    if (auth0Token == null) return
    setFleetLoading(true)
    setFleetError(null)
    try {
      const { months } = await graphqlRequest<{ months: FleetMonth[] }>(
        getFleetUsageQuery, {}, auth0Token
      )
      setFleetMonths(months)
    } catch (err) {
      setFleetError(err instanceof Error ? err.message : 'Failed to load the fleet roll-up')
    } finally {
      setFleetLoading(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void loadFleet()
  }, [loadFleet])

  const loadHistory = useCallback(async (accountId: string, force: boolean): Promise<void> => {
    if (auth0Token === undefined) return
    if (!force) {
      const cached = historyCacheRef.current.get(accountId)
      if (cached !== undefined) {
        setHistory(cached)
        setHistoryError(null)
        return
      }
    }
    setHistory(null)
    setHistoryLoading(true)
    setHistoryError(null)
    try {
      const { history: rows } = await graphqlRequest<{ history: AccountUsageHistoryRow[] }>(
        getAccountUsageHistoryQuery, { accountId }, auth0Token
      )
      historyCacheRef.current.set(accountId, rows)
      setHistory(rows)
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Failed to load usage history')
    } finally {
      setHistoryLoading(false)
    }
  }, [auth0Token])

  const openDrillDown = useCallback((row: AccountUsageRow): void => {
    setSelected(row)
    setSelectedMonth(monthKeyOf(new Date()))
    void loadHistory(row.accountId, false)
  }, [loadHistory])

  const handleSuspend = useCallback(async (row: AccountUsageRow, suspended: boolean): Promise<void> => {
    if (auth0Token === undefined || suspendBusyId !== null) return
    const who = emailOf(row) ?? 'this account'
    const ok = globalThis.confirm(suspended
      ? `Suspend ${who}? Serving gates refuse and pasted overlay URLs go dark at their next load. ${RUNBOOK_REMINDER}`
      : `Unsuspend ${who}? Serving gates reopen. Reinstated users re-copy their overlay links (the suspend-time token bump is permanent). ${RUNBOOK_REMINDER}`)
    if (!ok) return
    setSuspendBusyId(row.accountId)
    setSuspendError(null)
    setSuspendNotice(null)
    try {
      const { result } = await graphqlRequest<{ result: SetAccountSuspendedResult }>(
        setAccountSuspendedMutation, { accountId: row.accountId, suspended }, auth0Token
      )
      const patchPage = (prev: AccountUsagePage | null): AccountUsagePage | null => prev == null
        ? prev
        : {
            ...prev,
            items: prev.items.map((a) => a.accountId === row.accountId ? { ...a, suspendedAt: result.suspendedAt } : a)
          }
      setAccountsPage(patchPage)
      setOutliersPage(patchPage)
      setSelected((prev) => prev !== null && prev.accountId === row.accountId
        ? { ...prev, suspendedAt: result.suspendedAt }
        : prev)
      const label = who === 'this account' ? 'Account' : who
      const text = result.suspended
        ? `${label} suspended.${result.previouslySuspended ? ' (was already suspended)' : ''} Pasted overlay URLs die at their next load. ${RUNBOOK_REMINDER}`
        : `${label} unsuspended.${result.previouslySuspended ? '' : ' (was not suspended)'}`
      setSuspendNotice({ text, suspended: result.suspended })
    } catch (err) {
      setSuspendError(err instanceof Error ? err.message : 'Failed to set account suspension')
    } finally {
      setSuspendBusyId(null)
    }
  }, [auth0Token, suspendBusyId])

  const onSuspend = useCallback((row: AccountUsageRow, suspended: boolean): void => {
    void handleSuspend(row, suspended)
  }, [handleSuspend])

  const loadDeletions = useCallback(async (stack: string[]): Promise<void> => {
    if (auth0Token === undefined) return
    const requestId = deletionsRaceRef.current.begin()
    deletionsGateRef.current.hold()
    setDeletionsLoaded(true)
    setDeletionsLoading(true)
    setDeletionsError(null)
    try {
      const { page: fetched } = await graphqlRequest<{ page: AccountDeletionPage }>(
        listAccountDeletionsPageQuery,
        { limit: deletionsPageSize, nextToken: tokenForPage(stack), view: deletionsView },
        auth0Token
      )
      if (!deletionsRaceRef.current.isCurrent(requestId)) return
      setDeletionsPage(fetched)
      deletionsGateRef.current.settle(fetched.nextToken)
    } catch (err) {
      if (!deletionsRaceRef.current.isCurrent(requestId)) return
      deletionsGateRef.current.fail()
      setDeletionsError(err instanceof Error ? err.message : 'Failed to load deletion requests')
    } finally {
      if (deletionsRaceRef.current.isCurrent(requestId)) setDeletionsLoading(false)
    }
  }, [auth0Token, deletionsPageSize, deletionsView])

  const [deletionsReloadTick, setDeletionsReloadTick] = useState(0)

  useEffect(() => {
    if (!deletionsLoaded) return
    void loadDeletions(deletionsTokens)
  }, [loadDeletions, deletionsTokens, deletionsLoaded, deletionsReloadTick])

  const onDeletionsView = (view: string): void => {
    deletionsGateRef.current.drop()
    setDeletionsTokens(resetTokens())
    setDeletionsLoading(true)
    setParam('dview', view)
  }

  const onDeletionsPageSize = (size: number): void => {
    deletionsGateRef.current.drop()
    setDeletionsTokens(resetTokens())
    setDeletionsLoading(true)
    setParam('dsize', String(size))
  }

  const loadScheduled = useCallback(async (): Promise<void> => {
    if (auth0Token == null) return
    const requestId = scheduledRaceRef.current.begin()
    setScheduledLoading(true)
    setScheduledError(null)
    try {
      const { rows } = await graphqlRequest<{ rows: ScheduledAccountDeletion[] }>(
        listScheduledAccountDeletionsQuery, {}, auth0Token
      )
      if (!scheduledRaceRef.current.isCurrent(requestId)) return
      setScheduledRows(rows)
    } catch (err) {
      if (!scheduledRaceRef.current.isCurrent(requestId)) return
      setScheduledError(err instanceof Error ? err.message : 'Failed to load the scheduled deletions')
    } finally {
      if (scheduledRaceRef.current.isCurrent(requestId)) setScheduledLoading(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void loadScheduled()
  }, [loadScheduled])

  const loadDeletePreview = useCallback(async (row: AccountUsageRow): Promise<void> => {
    if (auth0Token === undefined) return
    const requestId = previewRaceRef.current.begin()
    setDeletePreview(null)
    setDeletePreviewError(false)
    setDeletePreviewLoading(true)
    try {
      const { preview } = await graphqlRequest<{ preview: AccountDeletionPreview }>(
        previewAccountDeletionQuery, { targetAccountId: row.accountId }, auth0Token
      )
      if (!previewRaceRef.current.isCurrent(requestId)) return
      setDeletePreview(preview)
    } catch {
      if (!previewRaceRef.current.isCurrent(requestId)) return
      setDeletePreviewError(true)
    } finally {
      if (previewRaceRef.current.isCurrent(requestId)) setDeletePreviewLoading(false)
    }
  }, [auth0Token])

  const openDeleteDialog = useCallback((row: AccountUsageRow): void => {
    if (deleteBusyId !== null) return
    setDeleteError(null)
    setDeleteTarget(row)
    void loadDeletePreview(row)
  }, [deleteBusyId, loadDeletePreview])

  const closeDeleteDialog = useCallback((): void => {
    if (deleteBusyId !== null) return
    previewRaceRef.current.begin()
    setDeleteTarget(null)
    setDeleteError(null)
    setDeletePreview(null)
    setDeletePreviewLoading(false)
    setDeletePreviewError(false)
  }, [deleteBusyId])

  const handleConfirmDelete = useCallback(async (suspendedReviewed: boolean): Promise<void> => {
    const row = deleteTarget
    if (auth0Token === undefined || row === null || deleteBusyId !== null) return
    setDeleteBusyId(row.accountId)
    setDeleteError(null)
    setDeleteNotice(null)
    try {
      const { result } = await graphqlRequest<{ result: AccountDeletionStatus }>(
        requestAccountDeletionMutation, { accountId: row.accountId, suspendedReviewed }, auth0Token
      )
      const label = emailOf(row) ?? 'this account'
      setDeleteNotice(result.status === 'SCHEDULED'
        ? scheduledNotice(label, formatStamp(result.runAt))
        : runningNotice(label, result.status))
      previewRaceRef.current.begin()
      setDeleteTarget(null)
      setDeletePreview(null)
      void loadScheduled()
      setDeletionsReloadTick((tick) => tick + 1)
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to request the account deletion')
    } finally {
      setDeleteBusyId(null)
    }
  }, [auth0Token, deleteTarget, deleteBusyId, loadScheduled])

  const handleCancelScheduled = useCallback(async (row: ScheduledAccountDeletion): Promise<void> => {
    if (auth0Token === undefined || cancelBusyId !== null) return
    const label = row.email ?? 'this account'
    const ok = globalThis.confirm(cancelConfirmCopy(label))
    if (!ok) return
    setCancelBusyId(row.accountId)
    setCancelError(null)
    setCancelNotice(null)
    try {
      await graphqlRequest<{ result: AccountDeletionStatus }>(
        cancelAccountDeletionMutation, { targetAccountId: row.accountId }, auth0Token
      )
      setCancelNotice(cancelledNotice(label))
      void loadScheduled()
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel the scheduled deletion')
    } finally {
      setCancelBusyId(null)
    }
  }, [auth0Token, cancelBusyId, loadScheduled])

  const handleRetryDeletion = useCallback(async (deletion: AccountDeletionRow): Promise<void> => {
    if (auth0Token === undefined || retryBusyId !== null) return
    const ok = globalThis.confirm('Run this account deletion again? Every step is safe to repeat; steps that already finished find nothing left to delete.')
    if (!ok) return
    setRetryBusyId(deletion.accountId)
    setRetryError(null)
    try {
      await graphqlRequest<{ result: AccountDeletionStatus }>(
        retryAccountDeletionMutation, { accountId: deletion.accountId }, auth0Token
      )
      setDeletionsReloadTick((tick) => tick + 1)
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : 'Failed to retry the account deletion')
    } finally {
      setRetryBusyId(null)
    }
  }, [auth0Token, retryBusyId])

  const deletingIds = useMemo(
    () => new Set((deletionsPage?.items ?? []).filter((d) => DELETION_IN_FLIGHT_STATUSES.has(d.status)).map((d) => d.accountId)),
    [deletionsPage]
  )

  const scheduledIds = useMemo(
    () => new Set((scheduledRows ?? []).filter((r) => r.status === 'SCHEDULED').map((r) => r.accountId)),
    [scheduledRows]
  )
  const scheduledLine = scheduledTopLine(scheduledIds.size)

  if (streamToolsUser === null) return <div>Unauthorized</div>
  if (!superuser) return null
  if (auth0Token === undefined) return <LoadingPage />

  return (
    <div className='flex flex-1' data-testid='usage-page'>
      <main className='flex flex-1 flex-col overflow-y-auto px-4 py-4'>
        <header className='mb-6 flex items-center gap-3'>
          <ChartBarIcon className='size-6 text-brand' aria-hidden='true' />
          <h1 className='text-2xl font-semibold text-dm-text-primary'>Usage</h1>
        </header>

        {suspendNotice !== null && (
          <p
            data-testid='usage-suspend-result'
            className={suspendNotice.suspended ? 'mb-4 text-xs text-amber-300' : 'mb-4 text-xs text-emerald-300'}
          >
            {suspendNotice.text}
          </p>
        )}
        {suspendError !== null && (
          <div className='mb-4'>
            <ErrorInline onDismiss={() => setSuspendError(null)}>{suspendError}</ErrorInline>
          </div>
        )}
        {scheduledLine !== null && (
          <p data-testid='usage-scheduled-top-line' className='mb-4 text-xs text-amber-300'>
            {scheduledLine}
          </p>
        )}
        {deleteNotice !== null && (
          <p data-testid='usage-delete-result' className='mb-4 text-xs text-amber-300'>
            {deleteNotice}
          </p>
        )}
        {cancelNotice !== null && (
          <p data-testid='usage-cancel-result' className='mb-4 text-xs text-emerald-300'>
            {cancelNotice}
          </p>
        )}
        {cancelError !== null && (
          <div className='mb-4'>
            <ErrorInline onDismiss={() => setCancelError(null)}>{cancelError}</ErrorInline>
          </div>
        )}
        {retryError !== null && (
          <div className='mb-4'>
            <ErrorInline onDismiss={() => setRetryError(null)}>{retryError}</ErrorInline>
          </div>
        )}

        <AccountsCard
          page={accountsPage}
          loading={accountsLoading}
          error={accountsError}
          onRefresh={() => { void loadAccounts(accountsTokens, accountsSearch) }}
          activeSearch={accountsSearch}
          onSearch={onAccountsSearch}
          sort={accountsSearch != null ? '' : sort}
          onSortCost={onSortCost}
          onSortCreated={onSortCreated}
          filters={accountsFilterChips}
          onFilter={onFilter}
          filterNote={rankedFilterNote}
          urlNotice={accountsUrlNotice}
          pageSize={accountsPageSize}
          onPageSize={onAccountsPageSize}
          pageNumber={accountsSearch != null ? 1 : pageNumberOf(accountsTokens)}
          onPrev={() => {
            if (!accountsGateRef.current.takePrev()) return
            setAccountsLoading(true)
            setAccountsTokens((prev) => popToken(prev))
          }}
          onNext={() => {
            const token = accountsGateRef.current.takeNext()
            if (token == null) return
            setAccountsLoading(true)
            setAccountsTokens((prev) => pushToken(prev, token))
          }}
          suspendBusyId={suspendBusyId}
          onOpen={openDrillDown}
          onSuspend={onSuspend}
          deleteBusyId={deleteBusyId}
          deletingIds={deletingIds}
          scheduledIds={scheduledIds}
          onDelete={openDeleteDialog}
        />

        {selected !== null && (
          <DrillDownCard
            row={selected}
            history={history}
            loading={historyLoading}
            error={historyError}
            onRetry={() => { void loadHistory(selected.accountId, true) }}
            selectedMonth={selectedMonth}
            onSelectMonth={setSelectedMonth}
            onRefresh={() => { void loadHistory(selected.accountId, true) }}
            onClose={() => setSelected(null)}
            suspendBusy={suspendBusyId === selected.accountId}
            onSuspend={onSuspend}
          />
        )}

        <FleetCard
          months={fleetMonths}
          loading={fleetLoading}
          error={fleetError}
          onRefresh={() => { void loadFleet() }}
        />

        <OutliersCard
          page={outliersPage}
          loading={outliersLoading}
          error={outliersError}
          onRefresh={() => { void loadOutliers(outliersTokens) }}
          pageSize={outliersPageSize}
          onPageSize={(size) => {
            outliersGateRef.current.drop()
            setOutliersTokens(resetTokens())
            setOutliersLoading(true)
            setParam('osize', String(size))
          }}
          pageNumber={pageNumberOf(outliersTokens)}
          onPrev={() => {
            if (!outliersGateRef.current.takePrev()) return
            setOutliersLoading(true)
            setOutliersTokens((prev) => popToken(prev))
          }}
          onNext={() => {
            const token = outliersGateRef.current.takeNext()
            if (token == null) return
            setOutliersLoading(true)
            setOutliersTokens((prev) => pushToken(prev, token))
          }}
          suspendBusyId={suspendBusyId}
          onOpen={openDrillDown}
          onSuspend={onSuspend}
        />

        <ScheduledDeletionsCard
          rows={scheduledRows}
          loading={scheduledLoading}
          error={scheduledError}
          onRefresh={() => { void loadScheduled() }}
          cancelBusyId={cancelBusyId}
          onCancel={(r) => { void handleCancelScheduled(r) }}
        />

        <DeletionsCard
          page={deletionsPage}
          view={deletionsView}
          onView={onDeletionsView}
          loading={deletionsLoading}
          error={deletionsError}
          loaded={deletionsLoaded}
          onLoad={() => { void loadDeletions(deletionsTokens) }}
          pageSize={deletionsPageSize}
          onPageSize={onDeletionsPageSize}
          pageNumber={pageNumberOf(deletionsTokens)}
          onPrev={() => {
            if (!deletionsGateRef.current.takePrev()) return
            setDeletionsLoading(true)
            setDeletionsTokens((prev) => popToken(prev))
          }}
          onNext={() => {
            const token = deletionsGateRef.current.takeNext()
            if (token == null) return
            setDeletionsLoading(true)
            setDeletionsTokens((prev) => pushToken(prev, token))
          }}
          retryBusyId={retryBusyId}
          onRetryDeletion={(d) => { void handleRetryDeletion(d) }}
        />

        {deleteTarget !== null && (
          <DeleteAccountDialog
            key={deleteTarget.accountId}
            row={deleteTarget}
            preview={deletePreview}
            previewLoading={deletePreviewLoading}
            previewError={deletePreviewError}
            busy={deleteBusyId !== null}
            error={deleteError}
            onConfirm={(reviewed) => { void handleConfirmDelete(reviewed) }}
            onClose={closeDeleteDialog}
          />
        )}
      </main>
    </div>
  )
}
